import axios from 'axios';
import jwt_decode from 'jwt-decode';
import apiURL from '../apiURL';

export interface MotorPolicyRow {
  rowKey: string; motorPolicyID: number | null; vehicleID: number | null; vehicleOwnerID: number | null; motorVehicleID?: number | null;
  providerCode: string | null; policyNumber: string | null; customer: string; registration: string;
  cover: string | null; inceptionDate: string | null; expiryDate: string | null;
  totalPremium: number | null; riskPremium: number | null; currency: string | null;
  marketer: string | null; isInsurancePaymentCompleted: boolean | null; status: string | null; lastSyncedAtUtc: string | null;
}
export interface MotorFilters {
  search: string; status: string; paymentStatus: string; dateBasis: string;
  fromDate: string; toDate: string; expiryPreset: string;
}
export const emptyMotorFilters: MotorFilters = { search: '', status: '', paymentStatus: '', dateBasis: 'EXPIRY', fromDate: '', toDate: '', expiryPreset: '' };
export interface MotorPage { rows: MotorPolicyRow[]; totalCount: number; page: number; pageSize: number }
export interface MotorSyncSummary {
  quarantinedPolicies?: number; quarantinedOccurrences?: number;
  syncId: number; startedAtUtc: string; completedAtUtc: string | null; status: string;
  recordsDownloaded: number; recordsInserted: number; recordsUpdated: number; recordsUnchanged: number;
  recordsFailed: number; unresolvedRecords: number; errorCode: string | null;
}
export interface MotorSyncStatus { busy: boolean; errorCode: string | null; latest: MotorSyncSummary | null; lastSuccessfulAtUtc: string | null }
export interface MotorIssue { motorProviderReconciliationIssueID: number; providerPolicyNumber: string; reasonCode: string; candidateVehicleID: number | null }
export interface MotorVehicle {
  vehicleID: number; vehicleOwnerID: number; registration: string; customer: string; phone: string | null;
  address: string | null; notes: string | null; activeCustomer: boolean | null; vehicleType: string | null;
  insuranceType: string | null; tariffPremium: number | null; legacyPremiumPaid: number | null; legacyMarketer: string | null;
  make: string | null; model: string | null; yearOfRegistration: number | null; bodyType: string | null;
  color: string | null; chassisNumber: string | null; cubicCapacity: number | null; seats: number | null;
}
export interface MotorDetail { row: MotorPolicyRow; vehicle: MotorVehicle; drivers: string[]; policy: Record<string, unknown> | null; premium: Record<string, unknown> | null }
const base = apiURL() + 'motor/policies';
const headers = (token: string) => ({ Authorization: `Bearer ${token}` });
const params = (filter: MotorFilters) => Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== ''));
export const motorAccess = (token?: string, write = false) => {
  try {
    const c = jwt_decode<Record<string, unknown>>(token || '');
    const yes = (value: unknown) => ['true', '1'].includes(String(value).toLowerCase());
    return c.AccountType === 'STAFF' && yes(c.CanViewMotorInsurance) && (!write || yes(c.CanEditMotorInsurance));
  } catch { return false; }
};
export async function getMotorPolicies(token: string, filter: MotorFilters, page: number, pageSize: number, signal?: AbortSignal) {
  return (await axios.get<MotorPage>(base, { headers: headers(token), params: { ...params(filter), page, pageSize }, signal })).data;
}
export async function getMotorStatuses(token: string) { return (await axios.get<string[]>(base + '/statuses', { headers: headers(token) })).data; }
export async function getMotorSyncStatus(token: string) { return (await axios.get<MotorSyncStatus>(base + '/sync/status', { headers: headers(token) })).data; }
export async function startMotorSync(token: string) { await axios.post(base + '/sync', {}, { headers: headers(token) }); }
export async function getMotorDetail(token: string, rowKey: string, signal?: AbortSignal) {
  return (await axios.get<MotorDetail>(base + '/details/' + encodeURIComponent(rowKey), { headers: headers(token), signal })).data;
}
export async function getMotorIssues(token: string, page: number) {
  return (await axios.get<{ rows: MotorIssue[]; totalCount: number }>(base + '/issues', { headers: headers(token), params: { page } })).data;
}
export async function confirmMotorLink(token: string, issueId: number, vehicleID: number) {
  await axios.post(base + '/issues/' + issueId + '/confirm-link', { vehicleID }, { headers: headers(token) });
}
export async function getMotorVehicle(token: string, id: number) {
  return (await axios.get<MotorVehicle>(base + '/vehicles/' + id, { headers: headers(token) })).data;
}
export async function saveMotorNotes(token: string, rowKey: string, notes: string) {
  await axios.put(base + '/details/' + encodeURIComponent(rowKey) + '/notes', { notes }, { headers: headers(token) });
}
export async function exportMotorPolicies(token: string, filter: MotorFilters) {
  const response = await axios.get(base + '/export', { headers: headers(token), params: params(filter), responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a'); a.href = url; a.download = 'Motor-Policies.csv'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function motorError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return 'Your session has expired. Please sign in again.';
    if (error.response?.status === 409) return 'A sync is running or this policy already has a confirmed link. Refresh and try again.';
    if (error.response?.status === 403) return 'You do not have permission for this action.';
    if (error.response?.status === 400) return 'Please check the filters or selected vehicle.';
  }
  return 'Motor policies are unavailable. Please try again or contact support.';
}
